import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, ReplaySubject, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '@environment/environment';
import { TokenStoreService } from './token-store.service';

interface OAuthTokenResponse {
	access_token: string;
	refresh_token: string;
	expires_in: number;
	token_type: string;
}

/**
 * Exchanges the stored refresh_token for a fresh access_token + refresh_token
 * via hecate's /oauth/token endpoint (CN-8597 Phase 3).
 *
 * Confidential-client form: client_id + client_secret. Spring OAuth2 1.x in
 * hecate doesn't honor a NULL secret as "public client" via DB config, so the
 * SPA has to send the bcrypted-on-the-server secret. Shipping it in the
 * bundle is user-recoverable; that's acceptable for the dev rollout while we
 * plan a longer-term move to PKCE / a BFF token exchange. Hecate's
 * resource_ids = "C4" on the c4-client OAuth row scopes the issued JWTs.
 *
 * Concurrency: the {@link #refresh} observable is memoized as a hot
 * single-fire Observable until it completes or errors. Concurrent callers
 * (e.g. two parallel c4 requests both getting 401 at the same time) share the
 * same in-flight refresh and replay their original requests off the same new
 * token rather than each minting their own refresh request and racing.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {

	private inFlight: Observable<string> | null = null;

	constructor(
		private readonly http: HttpClient,
		private readonly tokenStore: TokenStoreService,
	) {}

	/**
	 * Returns an Observable that emits the new access_token after a successful
	 * refresh. All concurrent callers within the same refresh window share one
	 * in-flight request; subsequent calls after completion start a new one.
	 *
	 * Errors are surfaced unchanged so the interceptor can clear the store +
	 * bounce to /login when the refresh_token itself has expired.
	 */
	refresh(): Observable<string> {
		if (this.inFlight !== null) {
			return this.inFlight;
		}

		const refreshToken = this.tokenStore.getRefreshToken();
		if (refreshToken === null) {
			return throwError(() => new Error('TokenRefreshService: no refresh token to use'));
		}

		const body = new URLSearchParams();
		body.set('grant_type', 'refresh_token');
		body.set('refresh_token', refreshToken);
		body.set('client_id', environment.AUTH_CLIENT_ID);
		body.set('client_secret', environment.AUTH_CLIENT_SECRET);

		const headers = new HttpHeaders({
			'Content-Type': 'application/x-www-form-urlencoded',
		});

		this.inFlight = this.http
			.post<OAuthTokenResponse>(
				`${environment.AUTH_SERVER_URL}/oauth/token`,
				body.toString(),
				{ headers },
			)
			.pipe(
				tap(res => this.tokenStore.setTokens(res.access_token, res.refresh_token, res.expires_in)),
				map(res => res.access_token),
				catchError(err => {
					// Only clear on terminal client-side errors — a 4xx means
					// the refresh token itself is dead and storing it any
					// longer would just keep failing. Transient 5xx / network
					// failures (hecate flake, timeout, no connectivity) leave
					// the refresh token in place so a subsequent attempt can
					// retry instead of forcing a full re-login.
					const status = err?.status;
					if (typeof status === 'number' && status >= 400 && status < 500) {
						this.tokenStore.clear();
					}
					return throwError(() => err);
				}),
				// Last-line cleanup: the next caller after this one completes
				// (whether success or error) gets a fresh request, not the
				// memoized result.
				tap({
					complete: () => { this.inFlight = null; },
					error: () => { this.inFlight = null; },
				}),
				// Hot/replay: late subscribers within the same in-flight window
				// receive the result without re-triggering the POST.
				shareReplay({ bufferSize: 1, refCount: false }),
			);

		return this.inFlight;
	}
}
